import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useState } from "react";

/**
 * Print-optimized record view. Auto-triggers window.print() after content loads.
 * User can Cmd/Ctrl+P and Save as PDF from the browser dialog — no server-side PDF deps.
 */
export default function PrintRecord() {
  const { id } = useParams();
  const [rec, setRec] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get(`/records/${id}`).then(async (r) => {
      setRec(r.data);
      const c = await api.get(`/module-config/${r.data.module_id}`);
      setCfg(c.data);
    });
  }, [id]);

  useEffect(() => {
    if (rec && cfg) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [rec, cfg]);

  if (!rec || !cfg) return <div className="p-12 font-mono-plex text-sm">Loading…</div>;

  const recordType = cfg.record_types.find((t) => t.id === rec.record_type);

  return (
    <div className="min-h-screen bg-white text-black p-12 print:p-8" data-testid="print-record">
      <style>{`
        @media print {
          @page { size: letter; margin: 0.75in; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
      `}</style>
      <button
        onClick={() => window.print()}
        className="no-print fixed top-4 right-4 px-4 py-2 rounded-sm bg-primary text-white font-mono-plex text-xs uppercase tracking-widest"
        data-testid="print-btn"
      >
        Print / Save PDF
      </button>

      <header className="border-b-2 border-black pb-4 mb-8">
        <div className="text-xs font-mono-plex uppercase tracking-widest text-zinc-600">
          RuleAI.Systems · {cfg.name} · {recordType?.name}
        </div>
        <h1 className="text-3xl font-black mt-3 font-display leading-tight">{rec.title}</h1>
        <div className="mt-4 grid grid-cols-4 gap-4 text-xs font-mono-plex uppercase tracking-widest text-zinc-700">
          <div>
            <div className="text-zinc-500">ID</div>
            <div className="mt-0.5 text-black">{rec.id.slice(0, 8)}</div>
          </div>
          <div>
            <div className="text-zinc-500">Status</div>
            <div className="mt-0.5 text-black">{rec.status}</div>
          </div>
          <div>
            <div className="text-zinc-500">Priority</div>
            <div className="mt-0.5 text-black">{rec.priority}</div>
          </div>
          <div>
            <div className="text-zinc-500">Author</div>
            <div className="mt-0.5 text-black">{rec.author_name}</div>
          </div>
        </div>
      </header>

      <section className="prose max-w-none">
        <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-black">
{rec.description || "(no content)"}
        </pre>
      </section>

      {rec.assignee && (
        <section className="mt-8 pt-4 border-t border-zinc-300 text-sm">
          <span className="font-mono-plex uppercase tracking-widest text-zinc-500">Assignee ·</span>{" "}
          {rec.assignee}
        </section>
      )}

      <footer className="mt-12 pt-4 border-t border-zinc-300 text-[10px] font-mono-plex uppercase tracking-widest text-zinc-500 flex justify-between">
        <span>Generated {new Date().toLocaleString()}</span>
        <span>ruleai.systems</span>
      </footer>
    </div>
  );
}
